/**
 * Inverse-parse branches for the scalar filter shapes `buildConditionSql`
 * (`filterWhereBuilder.ts`) writes that the plain `<field> <op> <literal>`
 * matcher in `predicateToFilterCondition.ts` cannot read:
 *
 *  - `BETWEEN` — `<field> BETWEEN <a> AND <b>`; its `AND` used to trip the
 *    compound-predicate rejection, so a chip-written range reopened as raw text.
 *  - the PostgreSQL array operators — `<v> = ANY(<field>)`, `<v> <> ALL(<field>)`,
 *    `cardinality(<field>) <op> <n>`.
 *  - boolean literals — `TRUE` / `FALSE` on the engines whose boolean policy
 *    writes them (`sqlBooleanLiteral`: PostgreSQL, MySQL).
 *
 * Plus the scanning helpers the parser needs for them: top-level `AND` / `OR`
 * detection (outside quotes and parentheses) and redundant outer parentheses.
 *
 * Every branch reads back exactly the literal forms the emitter writes (text,
 * the MySQL `_utf8mb4 X'…'` text form, numbers, Oracle `TO_DATE(…)` temporal
 * literals), and keeps a shape only when re-emitting it with the chip's
 * emitter gives the predicate back (`reemits`, the JSON matcher's rule), so
 * Apply on an unchanged chip never rewrites the filter; anything else returns
 * `null`, never a guess.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { sqlBooleanLiteral } from '../../../../core/utils/sqlBooleanLiteral';
import { parseSqlTemporalLiteral } from '../../../../core/utils/sqlTemporalLiteral';
import type { FilterCondition } from '../../core/types/workspaceTypes';
import { buildConditionSql } from './filterWhereBuilder';
import { QUALIFIED_IDENT, QUALIFIED_IDENT_SRC } from './predicateIdentGrammar';
import { decodeTextLiteral } from './predicateJsonHelpers';
import { exactNumber, PLAIN_DECIMAL_RE } from './sqlNumericLiteral';

type Parsed = { field: string; condition: FilterCondition; valueDataType?: string };

/**
 * A standalone numeric literal (integer or decimal, optionally negative) that a
 * JS number holds exactly (`exactNumber`, the number inputs' rule too), else
 * `null`. `9007199254740993` would come back as `…992` — a chip re-applying
 * it would filter on another value. Leading / trailing zeros are fine (`1.50`
 * is `1.5`).
 */
export function parseNumberLiteral(raw: string): number | null {
  const t = raw.trim();
  return PLAIN_DECIMAL_RE.test(t) ? exactNumber(t) : null;
}

/**
 * `TRUE` / `FALSE` read back as a JS boolean — only on an engine whose boolean
 * policy writes those keywords, so re-emitting the value gives the same text.
 * SQL Server / Oracle / SQLite write `1` / `0`; those come back as numbers and
 * the caller maps them by the column's type (`upgradeReopenedCondition`).
 */
export function parseBooleanLiteral(raw: string, engine?: DatabaseEngineId): boolean | null {
  if (!engine) return null;
  const up = raw.trim().toUpperCase();
  for (const value of [true, false]) {
    if (up === (value ? 'TRUE' : 'FALSE') && sqlBooleanLiteral(value, engine) === up) return value;
  }
  return null;
}

/** A comparison value as the emitter writes it: temporal call, text, or number. */
export function parseComparisonLiteral(raw: string, engine?: DatabaseEngineId): string | number | null {
  return parseSqlTemporalLiteral(raw) ?? decodeTextLiteral(raw, engine) ?? parseNumberLiteral(raw);
}

/** The column type an Oracle `TO_DATE` / `TO_TIMESTAMP[_TZ]` literal is written for (`sqlTemporalLiteral`). */
export function temporalTypeOfLiteral(raw: string): string | undefined {
  const fn = /^\s*TO_(DATE|TIMESTAMP_TZ|TIMESTAMP)\s*\(/i.exec(raw)?.[1]?.toUpperCase();
  return fn === 'DATE' ? 'date' : fn === 'TIMESTAMP' ? 'timestamp' : fn ? 'timestamp with time zone' : undefined;
}

/**
 * The column type several temporal literals of one filter were written for:
 * any `TO_TIMESTAMP_TZ` makes it a time-zone column (the chip writes a
 * zone-less value there as `TO_TIMESTAMP`), else the first literal's.
 */
export function temporalTypeOfLiterals(raws: readonly string[]): string | undefined {
  const types = raws.map(temporalTypeOfLiteral);
  return types.find((t) => t === 'timestamp with time zone') ?? types.find(Boolean);
}

const TIGHT_PUNCTUATION = '(),=<>';

/**
 * `s` as compared by `reemits`: outside quoted text and quoted identifiers,
 * whitespace runs become one space (none next to `( ) , = < >`) and letters
 * are upper-cased. Quoted text is kept byte for byte.
 */
function sqlShape(s: string): string {
  let out = '';
  let quote: string | null = null;
  let space = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (quote) {
      out += ch;
      if (ch === quote) {
        if (s[i + 1] === quote) out += s[++i];
        else quote = null;
      }
      continue;
    }
    if (/\s/.test(ch)) {
      space = out.length > 0;
      continue;
    }
    if (space && !TIGHT_PUNCTUATION.includes(ch) && !TIGHT_PUNCTUATION.includes(out[out.length - 1]!)) out += ' ';
    space = false;
    if (ch === "'" || ch === '"' || ch === '`') quote = ch;
    else if (ch === '[') quote = ']';
    out += quote ? ch : ch.toUpperCase();
  }
  return out;
}

/**
 * Whether the chip's emitter writes `parsed` back as `s` — up to spacing and
 * keyword case outside quotes. The rule `predicateJsonEmitMatch.ts` applies:
 * a shape reads back only when Apply on the unchanged chip keeps the filter,
 * so a concatenation that looked like a text bound, a rounded big integer or
 * an array operator on an engine without arrays stays raw text. `engine`
 * missing (single-argument callers) re-emits with plain ANSI quoting.
 */
export function reemits(s: string, parsed: Parsed, engine: DatabaseEngineId | undefined, valueDataType?: string): boolean {
  try {
    const sql = buildConditionSql(parsed.field, parsed.condition, engine ?? 'mock', valueDataType ? { valueDataType } : undefined);
    return sqlShape(sql) === sqlShape(s);
  } catch {
    return false;
  }
}

/**
 * Walk `s` at parenthesis depth 0, outside quoted text and quoted identifiers,
 * calling `visit(index, depth)` for every other character. Returns `false` for
 * unbalanced input.
 */
function walkUnquoted(s: string, visit: (index: number, depth: number) => void): boolean {
  let depth = 0;
  let quote: string | null = null;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (quote) {
      if (ch === quote) {
        // A doubled closer ('' "" `` ]]) is an escaped one inside the token.
        if (s[i + 1] === quote) i += 1;
        else quote = null;
      }
      continue;
    }
    if (ch === "'" || ch === '"' || ch === '`') quote = ch;
    else if (ch === '[') quote = ']';
    else if (ch === '(') depth += 1;
    else if (ch === ')') {
      depth -= 1;
      if (depth < 0) return false;
    } else visit(i, depth);
  }
  return depth === 0 && quote === null;
}

/** Start offsets of the `AND` / `OR` keywords at the top level of `s`, or `null` when unbalanced. */
export function topLevelKeywords(s: string, keyword: 'AND' | 'OR'): number[] | null {
  const hits: number[] = [];
  const isWord = (c: string | undefined) => !!c && /[A-Za-z0-9_$#@]/.test(c);
  const ok = walkUnquoted(s, (i, depth) => {
    if (depth !== 0 || isWord(s[i - 1])) return;
    if (s.slice(i, i + keyword.length).toUpperCase() === keyword && !isWord(s[i + keyword.length])) hits.push(i);
  });
  return ok ? hits : null;
}

/** Whether `s` joins two or more predicates with a top-level `AND` / `OR`. */
export function hasTopLevelConnector(s: string): boolean {
  return (topLevelKeywords(s, 'AND')?.length ?? 0) > 0 || (topLevelKeywords(s, 'OR')?.length ?? 0) > 0;
}

/**
 * The text inside one pair of parentheses that wraps ALL of `s` (`(a AND b)` →
 * `a AND b`), or `null` when `s` is not wrapped that way (`(a) AND (b)`).
 */
export function unwrapParentheses(s: string): string | null {
  const t = s.trim();
  if (!t.startsWith('(') || !t.endsWith(')')) return null;
  const inner = t.slice(1, -1);
  // The inner text must itself be balanced, else the first `(` closes early.
  return walkUnquoted(inner, () => {}) ? inner.trim() : null;
}

const BETWEEN_RE = new RegExp(`^(${QUALIFIED_IDENT_SRC})\\s+BETWEEN\\s+([\\s\\S]+)$`, 'i');

/** `<field> BETWEEN <a> AND <b>` → the `BETWEEN` operator with `value` / `value2`. */
export function parseBetweenPredicate(s: string, engine?: DatabaseEngineId): Parsed | null {
  const m = s.match(BETWEEN_RE);
  if (!m) return null;
  const field = m[1]!.trim();
  const bounds = m[2]!;
  const ands = topLevelKeywords(bounds, 'AND');
  // Exactly one top-level AND (a second one means `… BETWEEN 1 AND 2 AND x = 3`),
  // and no OR at all.
  if (!ands || ands.length !== 1 || topLevelKeywords(bounds, 'OR')?.length) return null;
  const low = bounds.slice(0, ands[0]);
  const high = bounds.slice(ands[0]! + 3);
  const value = parseComparisonLiteral(low, engine);
  const value2 = parseComparisonLiteral(high, engine);
  if (value === null || value2 === null || !QUALIFIED_IDENT.test(field)) return null;
  const valueDataType = temporalTypeOfLiterals([low, high]);
  const parsed: Parsed = { field, condition: { operator: 'BETWEEN', value, value2 }, ...(valueDataType ? { valueDataType } : {}) };
  if (reemits(s, parsed, engine, valueDataType)) return parsed;
  const textType = engine === 'mssql' && !valueDataType ? sqlServerTextTypeOf(s, parsed) : undefined;
  return textType ? { ...parsed, valueDataType: textType } : null;
}

/**
 * SQL Server writes a text value `N'…'` by the column type
 * (`sqlServerTextLiteral.ts`). On a column the schema does not resolve, the
 * type the predicate's literals were written for — `nvarchar` for `N` on ASCII
 * text, `varchar` for no `N` on text outside ASCII — so Apply on the unchanged
 * chip writes them back as they are. `undefined` when the no-type rule already
 * does, or when no text type does (hand-written SQL).
 */
export function sqlServerTextTypeOf(s: string, parsed: Parsed): string | undefined {
  if (reemits(s, parsed, 'mssql')) return undefined;
  return ['nvarchar', 'varchar'].find((type) => reemits(s, parsed, 'mssql', type));
}

const ARRAY_MEMBER_RE = new RegExp(`^([\\s\\S]+?)\\s*(=\\s*ANY|<>\\s*ALL)\\s*\\((${QUALIFIED_IDENT_SRC})\\)$`, 'i');
const CARDINALITY_RE = new RegExp(`^cardinality\\((${QUALIFIED_IDENT_SRC})\\)\\s*(<=|>=|<>|=|<|>)\\s*([\\s\\S]+)$`, 'i');
const LENGTH_OPS: Record<string, FilterCondition['operator']> = {
  '=': 'array_length_eq',
  '<>': 'array_length_neq',
  '>': 'array_length_gt',
  '<': 'array_length_lt',
  '>=': 'array_length_gte',
  '<=': 'array_length_lte',
};

/**
 * The array-column operators (`<v> = ANY(col)`, `<v> <> ALL(col)`,
 * `cardinality(col) <op> <n>`). `cardinality(col) = 0` / `> 0` read back as
 * "is empty" / "is not empty" — the length operators write the same text for
 * a 0 length, and the empty checks are what the chip offers for it.
 */
export function parseArrayPredicate(s: string, engine?: DatabaseEngineId): Parsed | null {
  const member = s.match(ARRAY_MEMBER_RE);
  if (member) {
    const raw = member[1]!;
    const value = decodeTextLiteral(raw, engine) ?? parseNumberLiteral(raw) ?? parseBooleanLiteral(raw, engine);
    if (value === null) return null;
    const operator = member[2]!.startsWith('=') ? 'array_contains' : 'array_not_contains';
    return verified({ field: member[3]!, condition: { operator, value } });
  }
  const card = s.match(CARDINALITY_RE);
  if (!card) return null;
  const [, field, op, raw] = card;
  const value = parseNumberLiteral(raw!) ?? decodeTextLiteral(raw!, engine);
  if (value === null) return null;
  if (value === 0 && (op === '=' || op === '>')) {
    return verified({ field: field!, condition: { operator: op === '=' ? 'array_is_empty' : 'array_is_not_empty' } });
  }
  return verified({ field: field!, condition: { operator: LENGTH_OPS[op!]!, value } });

  function verified(parsed: Parsed): Parsed | null {
    return reemits(s, parsed, engine) ? parsed : null;
  }
}
